/**
 * Obstruction State Selectors
 *
 * Atomic primitive selectors and a few derived ones for the obstructions
 * artifact. Consumers MUST subscribe to primitives or wrap object/array
 * results in `useShallow` (state.md atomic-selector rule).
 */
import type { GeoMarkupStore } from '../index';
import type {
  Obstruction,
  ObstructionListItem,
  ObstructionMapItem,
} from '@/types/obstructions';
import type { ApiError } from '@/types/api';

// ===== ATOMIC OBSTRUCTION STATE SELECTORS =====

/** All obstruction list items from the store. */
export const selectObstructionsItems = (
  state: GeoMarkupStore
): ObstructionListItem[] => state.obstructions.items;

/** Map-optimised obstructions used for marker rendering. */
export const selectObstructionsMapItems = (
  state: GeoMarkupStore
): ObstructionMapItem[] => state.obstructions.mapItems;

/** Currently selected obstruction id. */
export const selectObstructionsSelectedId = (
  state: GeoMarkupStore
): string | null => state.obstructions.selectedId;

/** Obstructions loading state (list-level). */
export const selectObstructionsLoading = (state: GeoMarkupStore): boolean =>
  state.obstructions.isLoading;

/** Whether the obstructions list has completed at least one fetch attempt. */
export const selectObstructionsHasLoaded = (state: GeoMarkupStore): boolean =>
  state.obstructions.hasLoaded;

/** Obstructions syncing state (per-item operations). */
export const selectObstructionsIsSyncing = (state: GeoMarkupStore): boolean =>
  state.obstructions.isSyncing;

/** Obstructions error state. */
export const selectObstructionsError = (
  state: GeoMarkupStore
): ApiError | null => state.obstructions.error;

// ===== DERIVED OBSTRUCTION SELECTORS =====

/**
 * Currently selected obstruction (list-item shape) — uses cached `items`
 * lookup. Full domain object is loaded via `fetchObstructionByShortId`.
 */
export const selectSelectedObstruction = (
  state: GeoMarkupStore
): ObstructionListItem | null => {
  const { items, selectedId } = state.obstructions;
  if (!selectedId) return null;
  return items.find((item) => item.id === selectedId) ?? null;
};

/** Full obstruction (with relations) loaded via `fetchObstructionByShortId`. */
export const selectDetailedObstruction = (
  state: GeoMarkupStore
): Obstruction | null => state.obstructions.detailedObstruction;

/** Whether there are more obstruction pages to load. */
export const selectObstructionsHasNextPage = (
  state: GeoMarkupStore
): boolean => state.obstructions.hasNextPage;

/** Total number of obstructions matching the current filters. */
export const selectObstructionsTotalCount = (state: GeoMarkupStore): number =>
  state.obstructions.totalCount;

/** Current 1-indexed page. */
export const selectObstructionsCurrentPage = (state: GeoMarkupStore): number =>
  state.obstructions.currentPage;

/**
 * Pagination summary as a convenience object.
 *
 * IMPORTANT: This selector returns a new object literal on every call — callers
 * MUST wrap the subscription in `useShallow(selectObstructionsPagination)` to
 * avoid unnecessary re-renders. Prefer the atomic primitives
 * (`selectObstructionsHasNextPage`, `selectObstructionsTotalCount`,
 * `selectObstructionsCurrentPage`) for direct subscriptions without `useShallow`.
 */
export const selectObstructionsPagination = (
  state: GeoMarkupStore
): {
  currentPage: number;
  totalCount: number;
  hasNextPage: boolean;
} => ({
  currentPage: state.obstructions.currentPage,
  totalCount: state.obstructions.totalCount,
  hasNextPage: state.obstructions.hasNextPage,
});

// ===== OBSTRUCTION UI SELECTORS =====

/** Whether the obstruction form is open. */
export const selectIsObstructionFormOpen = (state: GeoMarkupStore): boolean =>
  state.obstructions.formIsOpen;

/** Obstruction form mode (`create`, `edit`, or `null`). */
export const selectObstructionFormMode = (
  state: GeoMarkupStore
): 'create' | 'edit' | null => state.obstructions.formMode;

/** Whether the obstruction details panel is open. */
export const selectIsObstructionDetailOpen = (
  state: GeoMarkupStore
): boolean => state.obstructions.detailsIsOpen;
